import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminPageSkeleton } from "@/components/admin/AdminPageSkeleton";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import {
  Store,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Eye,
  ExternalLink,
  Trash2,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/utils/api";
import { toast } from "sonner";
import { formatPrice } from "@/utils/currency";

const BUSINESS_TYPES = [
  { id: "restaurant", label: "Restaurant & Cafe", icon: "🍽️" },
  { id: "retail", label: "Retail Store", icon: "🛍️" },
  { id: "beauty", label: "Beauty & Wellness", icon: "💄" },
  { id: "fitness", label: "Fitness & Sports", icon: "💪" },
  { id: "education", label: "Education & Training", icon: "📚" },
  { id: "healthcare", label: "Healthcare", icon: "🏥" },
  { id: "automotive", label: "Automotive", icon: "🚗" },
  { id: "home", label: "Home & Garden", icon: "🏠" },
  { id: "technology", label: "Technology", icon: "💻" },
  { id: "other", label: "Other", icon: "🏢" },
];

interface StoreData {
  _id: string;
  storeName: string;
  businessName: string;
  url: string;
  logo: string;
  currency: string;
  businessType: string;
  createdAt: string;
  owner: {
    _id: string;
    name: string;
    email: string;
  };
  analytics: {
    views: number;
    orders: number;
    revenue: number;
    totalRevenue: number;
    completedOrders: number;
    averageOrderValue: number;
    bookings?: number;
  };
}

interface StoreStats {
  totalStores: number;
  businessTypeStats: Record<string, number>;
  totalRevenue: number;
  totalOrders: number;
  completedRevenue: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalStores: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export function AllStoresPage() {
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [stats, setStats] = useState<StoreStats | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [businessTypeFilter, setBusinessTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("revenue");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchStores();
  }, [search, businessTypeFilter, sortBy, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    setSelectedStoreIds([]);
  }, [currentPage, search, businessTypeFilter]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sortBy,
        sortOrder,
      });

      if (search) params.append("search", search);
      if (businessTypeFilter && businessTypeFilter !== "all")
        params.append("businessType", businessTypeFilter);

      const response = await api.get(
        `/api/platform-analytics/stores/all?${params}`,
      );
      setStores(response.data.stores);
      setStats(response.data.stats);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching stores:", error);
      toast.error("Failed to fetch stores");
    } finally {
      setLoading(false);
    }
  };

  const getBusinessTypeLabel = (type: string) => {
    const businessType = BUSINESS_TYPES.find((bt) => bt.id === type);
    return businessType ? `${businessType.icon} ${businessType.label}` : type;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const clearFilters = () => {
    setSearch("");
    setBusinessTypeFilter("all");
    setCurrentPage(1);
  };

  const openStoreUrl = (url: string) => {
    window.open(`/${url}`, "_blank");
  };

  const storeIds = stores.map((s) => s._id);
  const allSelected =
    storeIds.length > 0 && storeIds.every((id) => selectedStoreIds.includes(id));

  const toggleSelectAll = (checked: boolean) => {
    setSelectedStoreIds(checked ? storeIds : []);
  };

  const toggleStoreSelection = (storeId: string, checked: boolean) => {
    setSelectedStoreIds((ids) =>
      checked ? [...ids, storeId] : ids.filter((id) => id !== storeId),
    );
  };

  const bulkDeleteStores = async () => {
    if (selectedStoreIds.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await api.post(
        "/api/platform-analytics/stores/bulk-delete",
        { storeIds: selectedStoreIds },
      );
      const { deleted, failed } = response.data;
      if (deleted?.length) {
        toast.success(
          `Deleted ${deleted.length} store${deleted.length === 1 ? "" : "s"} and their data`,
        );
      }
      if (failed?.length) {
        toast.error(
          `${failed.length} store${failed.length === 1 ? "" : "s"} could not be deleted`,
        );
      }
      setSelectedStoreIds([]);
      await fetchStores();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete stores",
      );
    } finally {
      setBulkDeleting(false);
      setShowBulkDeleteDialog(false);
    }
  };

  if (loading && !stores.length) {
    return <AdminPageSkeleton kpiCount={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stores"
        subtitle="Manage, monitor, and analyze all stores on the platform"
        actions={
          <Button
            onClick={fetchStores}
            variant="outline"
            className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold"
          >
            <Store className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <Card className="p-4">
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 w-full">
            <Card className="p-4 shadow-none border-none bg-purple-100/30 hover:bg-purple-100/80 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-black">
                    Total Stores
                  </p>
                  <div className="font-heading text-lg font-medium text-black">
                    {stats.totalStores}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <Store className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-purple-600 font-medium">
                      Platform-wide
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 shadow-none border-none bg-green-100/30 hover:bg-green-100/80 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-black">
                    Total Revenue
                  </p>
                  <div className="font-heading text-lg font-medium text-black">
                    {formatPrice(stats.completedRevenue, { currency: "NGN" })}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <DollarSign className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600 font-medium">
                      Completed
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 shadow-none border-none bg-blue-100/30 hover:bg-blue-100/80 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-black">
                    Total Orders
                  </p>
                  <div className="font-heading text-lg font-medium text-black">
                    {stats.totalOrders.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <ShoppingCart className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-blue-600 font-medium">
                      All stores
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4 shadow-none border-none bg-teal-100/30 hover:bg-teal-100/80 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-black">
                    Avg Order Value
                  </p>
                  <div className="font-heading text-lg font-medium text-black">
                    {stats.totalOrders > 0
                      ? formatPrice(
                          stats.completedRevenue / stats.totalOrders,
                          { currency: "NGN" },
                        )
                      : formatPrice(0, { currency: "NGN" })}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <TrendingUp className="h-4 w-4 text-teal-500" />
                    <span className="text-sm text-teal-600 font-medium">
                      Average
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search stores..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Business Type</label>
              <Select
                value={businessTypeFilter}
                onValueChange={setBusinessTypeFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {BUSINESS_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sort By</label>
              <Select
                value={`${sortBy}-${sortOrder}`}
                onValueChange={(value) => {
                  const [field, order] = value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue-desc">Highest Revenue</SelectItem>
                  <SelectItem value="revenue-asc">Lowest Revenue</SelectItem>
                  <SelectItem value="orders-desc">Most Orders</SelectItem>
                  <SelectItem value="orders-asc">Least Orders</SelectItem>
                  <SelectItem value="createdAt-desc">Newest First</SelectItem>
                  <SelectItem value="createdAt-asc">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Page Size</label>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full sm:w-auto"
              >
                Clear Filters
              </Button>
              {selectedStoreIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete {selectedStoreIds.length} selected
                </Button>
              )}
            </div>
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              Showing {stores.length} of {pagination?.totalStores || 0} stores
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stores Table */}
      <Card>
        <CardHeader>
          <CardTitle>Stores</CardTitle>
          <CardDescription>
            Complete list of all stores with analytics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto overflow-hidden rounded-[1.25rem] border border-[#EBEBEB]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      disabled={storeIds.length === 0}
                      onCheckedChange={(checked) =>
                        toggleSelectAll(checked === true)
                      }
                    />
                  </TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Analytics</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No stores found
                    </TableCell>
                  </TableRow>
                ) : (
                  stores.map((store) => (
                    <TableRow key={store._id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedStoreIds.includes(store._id)}
                          onCheckedChange={(checked) =>
                            toggleStoreSelection(store._id, checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {store.logo ? (
                            <img
                              src={store.logo}
                              alt={store.storeName}
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                              <Store className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div className="space-y-1">
                            <div className="font-medium">{store.storeName}</div>
                            <div className="text-sm text-muted-foreground">
                              {store.businessName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              /{store.url}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant="outline">
                          {getBusinessTypeLabel(store.businessType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-sm">
                            <Eye className="h-3 w-3 text-muted-foreground" />
                            {store.analytics.views.toLocaleString()} views
                          </div>
                          {typeof store.analytics.bookings === "number" && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              {store.analytics.bookings.toLocaleString()}{" "}
                              bookings
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            Avg:{" "}
                            {formatPrice(store.analytics.averageOrderValue, {
                              currency: store.currency,
                            })}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {formatPrice(store.analytics.revenue, {
                              currency: store.currency,
                            })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {store.analytics.completedOrders} completed
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(`/admin/stores/${store._id}`)
                            }
                          >
                            View Details
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openStoreUrl(store.url)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedStoreIds.length} store
              {selectedStoreIds.length === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected stores and all related
              data (products, orders, bookings, discounts). The store owners
              will keep their user accounts. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkDeleteStores}
              className="bg-red-600 hover:bg-red-700"
              disabled={bulkDeleting}
            >
              {bulkDeleting ? "Deleting..." : "Delete stores"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
